import httpService from '@Utils/httpService';

const baseUrl = 'api/sso';

export default {
  // ============================================
  // SSO Configuration Management
  // ============================================

  /**
   * Get SSO configuration for current company
   * @returns {Promise<SsoConfiguration>} SSO configuration including:
   *   - id: string - Configuration ID
   *   - companyId: string - Company ID
   *   - enabled: boolean - Whether SSO login is active
   *   - displayName: string - Display text for SSO button
   *   - azureTenantId: string - Azure AD Tenant ID
   *   - azureClientId: string - Azure AD Application ID
   *   - hasClientSecret: boolean - Whether client secret is configured
   *   - defaultRoleId: string - Default role for auto-provisioned users
   *   - defaultRoleName: string - Default role name
   *   - allowPasswordLogin: boolean - Allow email/password login alongside SSO
   *   - autoProvisionUsers: boolean - Auto-create users on first SSO login
   *   - allowedEmailDomains: string - Comma-separated allowed domains
   *   - useSharedApp: boolean - Using shared Azure app registration
   *   - organizationName: string - Organization name from Azure
   *   - consentGrantedAt: Date - When admin consent was granted
   *   - consentGrantedById: string - User ID who granted consent
   *   - consentGrantedByName: string - User name who granted consent
   *   - verifiedDomains: SsoVerifiedDomain[] - List of verified domains
   *   - createDate: Date - When configuration was created
   *   - updateDate: Date - When configuration was last updated
   */
  getSettings() {
    return httpService.get({ url: `${baseUrl}/configuration` });
  },

  /**
   * Update SSO configuration for current company
   * @param {Object} data - SSO configuration data
   * @param {string} [data.displayName] - Display name for SSO button
   * @param {string} [data.defaultRoleId] - Role assigned to auto-provisioned users
   * @param {boolean} [data.allowPasswordLogin] - Allow password login alongside SSO
   * @param {boolean} [data.autoProvisionUsers] - Auto-create users on first SSO login
   * @returns {Promise<SsoConfiguration>} Updated SSO configuration
   */
  updateSettings(data) {
    return httpService.put({ url: `${baseUrl}/configuration`, data });
  },

  /**
   * Delete SSO configuration for current company
   * Removes the Azure AD connection and disables SSO
   * @returns {Promise<{message: string}>} Delete result
   */
  deleteSettings() {
    return httpService.delete({ url: `${baseUrl}/configuration` });
  },

  /**
   * Test SSO connection to Azure AD
   * Verifies the tenant and app registration are accessible
   * @param {Object} data - Test connection data
   * @param {string} data.azureTenantId - Azure AD Tenant ID
   * @param {string} data.azureClientId - Azure AD Application Client ID
   * @param {string} data.azureClientSecret - Azure AD Application Client Secret
   * @returns {Promise<TestSsoConnectionResult>} Connection test result:
   *   - success: boolean - Whether connection succeeded
   *   - error: string|null - Error message if failed
   *   - tenantName: string|null - Azure tenant name
   *   - tenantId: string|null - Azure tenant ID
   *   - appDisplayName: string|null - Azure app display name
   */
  testConnection(data) {
    return httpService.post({ url: `${baseUrl}/configuration/test`, data });
  },

  /**
   * Disconnect SSO from the company
   * Clears Azure tenant connection but preserves verified domains
   * @returns {Promise<{message: string}>} Disconnect result
   */
  disconnect() {
    return httpService.post({ url: `${baseUrl}/configuration/disconnect` });
  },

  // ============================================
  // Authentication Settings
  // ============================================

  /**
   * Get authentication settings for current company
   * @returns {Promise<AuthenticationSettings>} Authentication settings:
   *   - enableSsoLogin: boolean - Whether SSO login is enabled
   *   - allowPasswordLogin: boolean - Whether password login is allowed alongside SSO
   */
  getAuthenticationSettings() {
    return httpService.get({ url: `${baseUrl}/configuration/authentication` });
  },

  /**
   * Update authentication settings for current company
   * @param {Object} data - Authentication settings
   * @param {boolean} data.enableSsoLogin - Enable/disable SSO login
   * @param {boolean} data.allowPasswordLogin - Allow/disallow password login alongside SSO
   * @returns {Promise<AuthenticationSettings>} Updated authentication settings
   */
  updateAuthenticationSettings(data) {
    return httpService.put({ url: `${baseUrl}/configuration/authentication`, data });
  },

  // ============================================
  // User Provisioning Settings
  // ============================================

  /**
   * Get user provisioning settings for current company
   * @returns {Promise<ProvisioningSettings>} Provisioning settings:
   *   - autoCreateUsers: boolean - Whether to auto-create users on first SSO login
   *   - defaultRoleId: string|null - Role ID assigned to auto-provisioned users
   *   - defaultRoleName: string|null - Display name of the default role (read-only)
   *   - facilityIds: string[] - Facility IDs assigned to auto-provisioned users
   */
  getProvisioningSettings() {
    return httpService.get({ url: `${baseUrl}/configuration/provisioning` });
  },

  /**
   * Update user provisioning settings for current company
   * @param {Object} data - Provisioning settings
   * @param {boolean} data.autoCreateUsers - Enable/disable auto-provisioning
   * @param {string} [data.defaultRoleId] - Role ID (required when autoCreateUsers is true)
   * @param {string[]} [data.facilityIds] - Facility IDs assigned to auto-provisioned users
   * @returns {Promise<ProvisioningSettings>} Updated provisioning settings
   */
  updateProvisioningSettings(data) {
    return httpService.put({ url: `${baseUrl}/configuration/provisioning`, data });
  },

  // ============================================
  // Admin Consent Flow
  // ============================================

  /**
   * Initiate admin consent flow for Azure AD
   * Returns a URL to redirect the admin to Microsoft's consent page
   * @returns {Promise<AdminConsentInitiateResult>} Consent initiation result:
   *   - consentUrl: string - URL to redirect admin to for consent
   *   - state: string - State token for verification
   */
  initiateAdminConsent() {
    return httpService.post({ url: `${baseUrl}/configuration/consent/initiate` });
  },

  // ============================================
  // Verified Domains Management
  // ============================================

  /**
   * Get all verified domains for the company's SSO configuration
   * @returns {Promise<SsoVerifiedDomain[]>} List of verified domains:
   *   - id: string - Domain ID (for deletion)
   *   - domain: string - The verified domain
   *   - isActive: boolean - Whether domain is active for SSO
   *   - notes: string|null - Optional notes about the domain
   *   - createDate: Date - When the domain was added
   *   - createdByName: string|null - Name of user who added the domain
   */
  getVerifiedDomains() {
    return httpService.get({ url: `${baseUrl}/configuration/domains` });
  },

  /**
   * Add a verified domain to the SSO configuration
   * Users with emails matching this domain can use SSO to login
   * @param {Object} data - Domain data
   * @param {string} data.domain - Domain to add (e.g., "contoso.com")
   * @param {string} [data.notes] - Optional notes about this domain
   * @returns {Promise<SsoVerifiedDomain>} The added domain
   */
  addVerifiedDomain(data) {
    return httpService.post({ url: `${baseUrl}/configuration/domains`, data });
  },

  /**
   * Remove a verified domain from the SSO configuration
   * Users with emails matching this domain will no longer be able to use SSO
   * @param {string} domainId - ID of the domain to remove
   * @returns {Promise<{message: string}>} Removal result
   */
  removeVerifiedDomain(domainId) {
    return httpService.delete({ url: `${baseUrl}/configuration/domains/${domainId}` });
  },

  // ============================================
  // Email SSO Check (Public)
  // ============================================

  /**
   * Check if SSO is available for an email address
   * Used by login page to determine login options
   * @param {string} email - User email address
   * @returns {Promise<CheckEmailSsoResult>} SSO availability info:
   *   - ssoAvailable: boolean - Whether SSO is configured for this email's domain
   *   - passwordLoginAllowed: boolean - Whether password login is also allowed
   *   - ssoDisplayName: string|null - Display text for SSO button
   *   - companyId: string|null - Company ID for SSO login redirect
   *   - userExists: boolean - Whether the user already exists in the system
   *   - canAutoProvision: boolean - Whether new user can be auto-provisioned via SSO
   *   - organizationName: string|null - Organization name from SSO configuration
   *   - error: string|null - Error message if SSO is not available
   */
  checkEmailSSO(email) {
    return httpService.post({ url: `${baseUrl}/check-email`, data: { email } });
  },
};
